import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Bell,
    Check,
    CheckCheck,
    Award,
    Trophy,
    Sparkles,
    Users,
    Megaphone,
    Flame,
    BellOff,
    Settings as SettingsIcon,
    Pin,
    UserPlus,
} from "lucide-react";
import api from "../lib/api";

const KIND_META = {
    streak_reminder: { icon: Flame, color: "text-shariti-orange", bg: "bg-shariti-orange/15" },
    inactivity_nudge: { icon: BellOff, color: "text-white/60", bg: "bg-white/10" },
    badge_earned: { icon: Award, color: "text-shariti-yellow", bg: "bg-shariti-yellow/15" },
    milestone: { icon: Sparkles, color: "text-shariti-orange-light", bg: "bg-shariti-orange/15" },
    weekly_winner: { icon: Trophy, color: "text-shariti-yellow", bg: "bg-shariti-yellow/15" },
    weekly_kickoff: { icon: Trophy, color: "text-shariti-orange", bg: "bg-shariti-orange/10" },
    cooptation_approved: { icon: Users, color: "text-emerald-400", bg: "bg-emerald-400/15" },
    social_proof_validated: { icon: Megaphone, color: "text-emerald-400", bg: "bg-emerald-400/15" },
    social_proof_rejected: { icon: Megaphone, color: "text-red-400", bg: "bg-red-400/15" },
    module_unlocked: { icon: Sparkles, color: "text-shariti-orange-light", bg: "bg-shariti-orange/10" },
    quiz_validated: { icon: Award, color: "text-shariti-yellow", bg: "bg-shariti-yellow/15" },
    admin_proof_pending: { icon: Bell, color: "text-shariti-orange", bg: "bg-shariti-orange/10" },
    comment_pinned: { icon: Pin, color: "text-shariti-yellow", bg: "bg-shariti-yellow/15" },
    signup_pending: { icon: UserPlus, color: "text-shariti-orange", bg: "bg-shariti-orange/15" },
};

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `il y a ${Math.floor(diff / 86400)} j`;
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export default function NotificationsBell({ className = "" }) {
    const [open, setOpen] = useState(false);
    const [notifs, setNotifs] = useState([]);
    const [unread, setUnread] = useState(0);
    const ref = useRef(null);
    const navigate = useNavigate();

    const fetchNotifs = async () => {
        try {
            const r = await api.get("/me/notifications");
            setNotifs(r.data.notifications || []);
            setUnread(r.data.unread_count || 0);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchNotifs();
        const t = setInterval(fetchNotifs, 60_000); // poll every 60s
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const markRead = async (notif) => {
        if (!notif.read_at) {
            try {
                await api.post(`/me/notifications/${notif.notif_id}/read`);
                setUnread((u) => Math.max(0, u - 1));
                setNotifs((arr) =>
                    arr.map((n) =>
                        n.notif_id === notif.notif_id
                            ? { ...n, read_at: new Date().toISOString() }
                            : n,
                    ),
                );
            } catch {}
        }
        if (notif.url) {
            setOpen(false);
            navigate(notif.url);
        }
    };

    const markAllRead = async () => {
        try {
            await api.post("/me/notifications/read-all");
            setUnread(0);
            setNotifs((arr) =>
                arr.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })),
            );
        } catch {}
    };

    return (
        <div ref={ref} className={`relative ${className}`}>
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="notifications-button"
                aria-label="Notifications"
                className="relative w-11 h-11 flex items-center justify-center rounded-2xl glass-card hover:border-shariti-orange/40 transition-colors"
            >
                <Bell className="w-5 h-5 text-white" />
                {unread > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-shariti-orange text-white text-[10px] font-extrabold flex items-center justify-center shadow-lg"
                        data-testid="notif-badge"
                    >
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <>
                        {/* Mobile-only backdrop to close on tap outside */}
                        <motion.button
                            type="button"
                            aria-label="Fermer le panneau"
                            onClick={() => setOpen(false)}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="sm:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
                            data-testid="notif-backdrop"
                        />
                        <motion.div
                            initial={{ opacity: 0, y: -8, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -8, scale: 0.97 }}
                            transition={{ duration: 0.15 }}
                            // Mobile: full-width sheet positioned just under the
                            // header (fixed, so it never overflows horizontally).
                            // Desktop: classic dropdown anchored to the bell.
                            className="fixed top-20 left-3 right-3 sm:absolute sm:top-12 sm:left-auto sm:right-0 sm:w-[360px] sm:max-w-none rounded-2xl border border-white/10 bg-shariti-deep-black/95 backdrop-blur-xl shadow-2xl z-50 overflow-hidden"
                            data-testid="notifications-panel"
                        >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                            <p className="text-sm font-extrabold text-white">
                                Notifications
                                {unread > 0 && (
                                    <span className="text-shariti-orange-light ml-1.5">
                                        · {unread} non lue{unread > 1 ? "s" : ""}
                                    </span>
                                )}
                            </p>
                            <div className="flex items-center gap-1">
                                {unread > 0 && (
                                    <button
                                        onClick={markAllRead}
                                        data-testid="notif-mark-all-read"
                                        title="Tout marquer comme lu"
                                        className="text-[10px] uppercase tracking-widest font-bold text-shariti-orange-light hover:text-white transition-colors flex items-center gap-1"
                                    >
                                        <CheckCheck className="w-3 h-3" />
                                        Tout lu
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        setOpen(false);
                                        navigate("/profil#notifications");
                                    }}
                                    title="Réglages des notifications"
                                    className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/5"
                                >
                                    <SettingsIcon className="w-3.5 h-3.5 text-white/40" />
                                </button>
                            </div>
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto">
                            {notifs.length === 0 ? (
                                <div className="py-10 px-6 text-center">
                                    <Bell className="w-8 h-8 text-white/15 mx-auto mb-3" />
                                    <p className="text-sm text-white/40 font-light">
                                        Aucune notification pour l'instant.
                                    </p>
                                </div>
                            ) : (
                                notifs.map((n) => {
                                    const meta =
                                        KIND_META[n.kind] || {
                                            icon: Bell,
                                            color: "text-white/60",
                                            bg: "bg-white/10",
                                        };
                                    const Icon = meta.icon;
                                    const isUnread = !n.read_at;
                                    return (
                                        <button
                                            key={n.notif_id}
                                            onClick={() => markRead(n)}
                                            data-testid={`notif-item-${n.notif_id}`}
                                            className={`w-full flex gap-3 px-4 py-3 text-left border-b border-white/5 hover:bg-white/3 transition-colors ${
                                                isUnread ? "bg-white/[0.02]" : "opacity-70"
                                            }`}
                                        >
                                            <div
                                                className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.bg}`}
                                            >
                                                <Icon className={`w-4 h-4 ${meta.color}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start gap-2">
                                                    <p
                                                        className={`text-sm font-extrabold leading-snug flex-1 min-w-0 ${
                                                            isUnread
                                                                ? "text-white"
                                                                : "text-white/70"
                                                        }`}
                                                    >
                                                        {n.title}
                                                    </p>
                                                    {isUnread && (
                                                        <span className="w-2 h-2 rounded-full bg-shariti-orange shrink-0 mt-1.5" />
                                                    )}
                                                </div>
                                                <p className="text-xs text-white/55 font-light leading-relaxed mt-0.5 line-clamp-2">
                                                    {n.body}
                                                </p>
                                                <p className="text-[10px] uppercase tracking-widest font-bold text-white/30 mt-1">
                                                    {timeAgo(n.created_at)}
                                                </p>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
